/* global document:true, localStorage, window */

import React, { Fragment } from 'react';
import { ConfigProvider } from 'antd';
import { PersistGate } from 'redux-persist/integration/react';
import { Router } from 'react-router-dom';
import { Provider } from 'react-redux';
import Routes from './core/routes';
import history from './core/utils/history';
import appConstants from './core/constants';
import { AwsRum } from 'aws-rum-web';
import * as Sentry from "@sentry/react";
import { createRoot } from 'react-dom/client';
import { setVersion } from './core/global/globalActions';
import pack from '../package.json';
import configureStore from './core/store';

// Load global styles
import './styles/style.css';


const { SENTRY_DSN } = appConstants;
const sentryReduxEnhancer = Sentry.createReduxEnhancer({});
const globalStore = configureStore({
    enhancers: (getDefaultEnhancers) => {
        return getDefaultEnhancers().concat(sentryReduxEnhancer);
    },
});
const { store, persistor } = globalStore;

// Set the version in the global state
const VERSION = pack.version;
store.dispatch(setVersion(VERSION));

// Clear cache to load new webapp changes
const cachedVersion = localStorage.getItem('appVersion');
if (cachedVersion !== VERSION) {
    if (cachedVersion) {
        localStorage.clear();
        if (caches in window) {
            caches.keys().then((names) => {
                // Delete all the cache files
                names.forEach((name) => {
                    caches.delete(name);
                });
            });
        }
        window.location.reload(true);
    }
    localStorage.setItem('appVersion', VERSION);
}

// Setup AWS Realtime User Monitoring
try {
    const config = {
        sessionSampleRate: 1,
        identityPoolId: "eu-west-1:a4eb5c4e-c500-430c-9b81-456e608d52dc",
        endpoint: "https://dataplane.rum.eu-west-1.amazonaws.com",
        telemetries: ["performance", "errors", "http"],
        allowCookies: true,
        enableXRay: false
    };
  
    const APPLICATION_ID = '7fef1277-a86b-410c-9f14-4f138d577405';
    const APPLICATION_VERSION = '1.0.0';
    const APPLICATION_REGION = 'eu-west-1';

    const awsRum = new AwsRum(
        APPLICATION_ID,
        APPLICATION_VERSION,
        APPLICATION_REGION,
        config
    );
} catch (error) {
    // Ignore errors thrown during CloudWatch RUM web client initialization?
}
// Render with React 18
const container = document.getElementById('root');
const root = createRoot(container);

if (SENTRY_DSN !== null) {
    Sentry.init({
        dsn: SENTRY_DSN,
        integrations: [
            Sentry.replayIntegration(),
            Sentry.browserTracingIntegration(),
            Sentry.captureConsoleIntegration(),
            Sentry.reportingObserverIntegration(),
            Sentry.breadcrumbsIntegration({ console: false }),
        ],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        tracesSampleRate: 1.0, // Adjust in production to reduce data volume
    });
}



root.render(
    <Provider store={store}>
        <PersistGate
            loading={null}
            persistor={persistor}
        >
            <ConfigProvider theme={{ hashed: false }}>
                <Router
                    history={history}
                    basename="/"
                >
                    <Routes />
                </Router>
            </ConfigProvider>
        </PersistGate>
    </Provider>
);
